import { FC, useMemo, useRef, useState, type DragEventHandler } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, IconButton, InputAdornment, Menu, MenuItem, Tooltip } from "@mui/material";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { DeleteIcon, LocationIcon } from "../../../../../assets/icons";
import FormTextField from "../../../../../components/forms/FormTextField";
import { getNodeTypeIcon } from "../../../../../constants/nodeTypeIcons";
import { GeocodeRequest } from "../../../../../datasources/carbonsnap/io/requests";
import {
	CarbonSnapGeocodeResponse,
	CarbonSnapGeocodeResult,
	CarbonSnapSearchNode,
	CarbonSnapSearchType,
	SearchCarbonSnapNodesResponse,
} from "../../../../../datasources/carbonsnap/io/response";
import {
	CarbonSnapFormData,
	DEFAULT_NODE_TYPE_IDS,
	NodeCodeType,
	TransportNode as TransportNodeModel,
} from "../../../../../datasources/carbonsnap/models";
import { resolveNodeType } from "../../../../../datasources/carbonsnap/utils";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { GetOptionsTopic } from "../../../../../datasources/requests/common";
import { useServerOptions } from "../../../../../hooks/useOptions";
import { useReadResource } from "../../../../../hooks/useResource";
import AddressAutocomplete from "./AddressAutocomplete";

type GeolocationResult = {
	title: string;
	address: {
		label: string;
		countryCode: string;
		countryName: string;
		city?: string;
		street?: string;
		postalCode?: string;
		state?: string;
	};
	position: {
		lat: number;
		lng: number;
	};
	type: "geolocation";
	code?: string;
	codeType?: "geolocation";
	nodeTypeId?: number;
	nodeTypeName?: string;
	emissions?: number;
};

type AutosuggestResult = CarbonSnapSearchNode | GeolocationResult;

/**
 * Width of the leading drag-handle + node-type icon cluster and of the trailing delete icon button.
 * ArcControls uses the same values so its content lines up under the address input above it.
 */
export const ROUTE_NODE_LEADING_WIDTH = 88;
export const ROUTE_NODE_TRAILING_WIDTH = 40;

interface Props {
	form: UseFormReturn<CarbonSnapFormData>;
	nodeIndex: number;
	isFirst: boolean;
	isLast: boolean;
	isLoading?: boolean;
	canDelete: boolean;
	onDelete: () => void;
	dragHandleProps?: {
		draggable?: boolean;
		onDragStart?: DragEventHandler<HTMLButtonElement>;
		onDragEnd?: DragEventHandler<HTMLButtonElement>;
	};
}

const RouteNodeInput: FC<Props> = ({
	form,
	nodeIndex,
	isFirst,
	isLast,
	isLoading,
	canDelete,
	onDelete,
	dragHandleProps,
}) => {
	const { t } = useTranslation("feature.carbonsnap");
	const { data: nodeTypesData } = useServerOptions(GetOptionsTopic.NodeTypes);
	const nodeTypeOptions = nodeTypesData?.options ?? [];
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [currentSearchMode, setCurrentSearchMode] = useState<NodeCodeType>("network");
	const textFieldRef = useRef<HTMLDivElement>(null);
	const [nodeTypeAnchor, setNodeTypeAnchor] = useState<HTMLElement | null>(null);

	const [debouncedQuery] = useDebounce(searchQuery, 1000);

	const node = useWatch({
		control: form.control,
		name: `nodes.${nodeIndex}`,
	}) as TransportNodeModel | undefined;

	const trimmedQuery = debouncedQuery?.trim() ?? "";
	const isGeolocationMode = currentSearchMode === "geolocation";
	const shouldSearch = trimmedQuery.length > 0;

	// Query for backend geocoding (geolocation mode)
	const geocodeQuery = useReadResource<GeocodeRequest, CarbonSnapGeocodeResponse>(
		{ route: ApiRoutes.CarbonSnapGeocode },
		{
			readMethod: "post",
			disabled: !shouldSearch || !isGeolocationMode,
			emptyFallback: [],
		},
		{ address: trimmedQuery }
	);

	const nodeSearchQuery = useReadResource<undefined, SearchCarbonSnapNodesResponse>(
		{
			route: ApiRoutes.CarbonSnapNodesSearch,
			query:
				shouldSearch && !isGeolocationMode
					? {
							type: currentSearchMode as CarbonSnapSearchType,
							q: trimmedQuery,
						}
					: undefined,
		},
		{
			emptyFallback: [],
			disabled: !shouldSearch || isGeolocationMode,
		}
	);

	const activeResults = isGeolocationMode
		? (geocodeQuery.data?.map<AutosuggestResult>((item: CarbonSnapGeocodeResult) => ({
				title: item.title,
				address: item.address,
				position: item.position,
				type: "geolocation",
				codeType: "geolocation",
			})) ?? [])
		: ((nodeSearchQuery.data ?? []) as AutosuggestResult[]);
	const isSearching = isGeolocationMode ? geocodeQuery.isLoading : nodeSearchQuery.isLoading;

	const isLoadingOrDebouncing = (searchQuery.length >= 1 && searchQuery !== debouncedQuery) || isSearching;

	const handleAddressChange = (value: string) => {
		if (currentSearchMode === "geolocation") {
			setCurrentSearchMode("network");
		}

		setSearchQuery(value);

		if (value.length >= 1 && textFieldRef.current) {
			setAnchorEl(textFieldRef.current);
		} else {
			setAnchorEl(null);
		}
	};

	const handleGeolocationClick = () => {
		const value = form.getValues(`nodes.${nodeIndex}.address`);
		setCurrentSearchMode("geolocation");
		setSearchQuery(value ? `${value}` : "");
		setAnchorEl(textFieldRef.current);
	};

	const handleTabChange = (mode: NodeCodeType) => {
		setCurrentSearchMode(mode);
		const value = form.getValues(`nodes.${nodeIndex}.address`);
		if (value && value.length >= 1) {
			setSearchQuery(value);
		}
	};

	const handleSelectAddress = (result: AutosuggestResult) => {
		const nodesCount = (form.getValues("nodes") ?? []).length;
		const fallbackType = resolveNodeType(result.codeType, nodeIndex, nodesCount);
		form.setValue(`nodes.${nodeIndex}.address`, result.address.label, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.lat`, result.position.lat, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.lng`, result.position.lng, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.city`, result.address.city, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.zipCode`, result.address.postalCode, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.province`, result.address.state, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.country`, result.address.countryName, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.code`, result.code, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.codeType`, result.codeType, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.name`, result.type === "network" ? result.title : undefined, {
			shouldDirty: true,
		});
		form.setValue(`nodes.${nodeIndex}.nodeTypeId`, result.nodeTypeId ?? fallbackType.nodeTypeId, {
			shouldDirty: true,
		});
		form.setValue(`nodes.${nodeIndex}.nodeTypeName`, result.nodeTypeName ?? fallbackType.nodeTypeName, {
			shouldDirty: true,
		});
		form.setValue(`nodes.${nodeIndex}.emissions`, result.emissions, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.isGeolocated`, result.type === "geolocation", { shouldDirty: true });

		setAnchorEl(null);

		setCurrentSearchMode("network");
	};

	const handleCloseAutocomplete = () => {
		setAnchorEl(null);
	};

	const handleClearInput = () => {
		form.setValue(`nodes.${nodeIndex}.address`, "", { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.lat`, null, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.lng`, null, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.city`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.zipCode`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.province`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.country`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.code`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.codeType`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.name`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.emissions`, undefined, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.isGeolocated`, undefined, { shouldDirty: true });
		setSearchQuery("");
		setAnchorEl(null);
		setCurrentSearchMode("network");
	};

	const NodeIcon = useMemo(() => {
		if (node?.nodeTypeId) {
			return getNodeTypeIcon(node.nodeTypeId);
		}
		if (isFirst) return getNodeTypeIcon(DEFAULT_NODE_TYPE_IDS.centralWarehouse);
		if (isLast) return getNodeTypeIcon(DEFAULT_NODE_TYPE_IDS.customer);
		return LocationIcon;
	}, [isFirst, isLast, node?.nodeTypeId]);

	const nodeTypeLabel =
		node?.nodeTypeName ??
		(isFirst
			? t("route/node/default/central warehouse")
			: isLast
				? t("route/node/default/customer")
				: t("route/node/default/transit point"));

	const handleOpenNodeTypeMenu = (event: React.MouseEvent<HTMLElement>) => {
		setNodeTypeAnchor(event.currentTarget);
	};

	const handleCloseNodeTypeMenu = () => {
		setNodeTypeAnchor(null);
	};

	const handleSelectNodeType = (nodeTypeId: number, nodeTypeName: string) => {
		form.setValue(`nodes.${nodeIndex}.nodeTypeId`, nodeTypeId, { shouldDirty: true });
		form.setValue(`nodes.${nodeIndex}.nodeTypeName`, nodeTypeName, { shouldDirty: true });
		handleCloseNodeTypeMenu();
	};

	return (
		<Box sx={{ mb: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						flexShrink: 0,
						width: ROUTE_NODE_LEADING_WIDTH,
						height: "40px",
					}}
				>
					<Tooltip title={t("route/node/reorder")}>
						<IconButton
							size="small"
							sx={{ cursor: "grab", height: "40px" }}
							{...dragHandleProps}
							aria-label={t("route/node/reorder")}
						>
							<DragIndicatorIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title={nodeTypeLabel}>
						<IconButton
							size="small"
							onClick={handleOpenNodeTypeMenu}
							aria-label={t("route/node/node type")}
							sx={{
								border: 1,
								borderColor: "divider",
								borderRadius: 1,
								px: 0.5,
								height: "40px",
							}}
						>
							{NodeIcon && <NodeIcon fontSize="small" color="secondary" />}
							<ArrowDropDownIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
				<Menu anchorEl={nodeTypeAnchor} open={Boolean(nodeTypeAnchor)} onClose={handleCloseNodeTypeMenu}>
					{nodeTypeOptions.map((option) => {
						const OptionIcon = getNodeTypeIcon(Number(option.id));
						return (
							<MenuItem
								key={option.id}
								onClick={() => handleSelectNodeType(Number(option.id), option.label)}
								selected={Number(option.id) === node?.nodeTypeId}
							>
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
									{OptionIcon && <OptionIcon fontSize="small" color="secondary" />}
									{option.label}
								</Box>
							</MenuItem>
						);
					})}
				</Menu>

				<Box ref={textFieldRef} sx={{ flexGrow: 1, minWidth: 0 }}>
					<FormTextField
						name={`nodes.${nodeIndex}.address`}
						label=""
						form={form}
						isLoading={isLoading}
						fullWidth
						placeholder={t("route/node/placeholder")}
						sx={{ height: "40px" }}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										{Boolean(form.getValues(`nodes.${nodeIndex}.address`)) && (
											<IconButton
												edge="end"
												onClick={handleClearInput}
												size="small"
												color="default"
												aria-label={t("route/node/clear")}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										)}
										<IconButton
											edge="end"
											onClick={handleGeolocationClick}
											size="small"
											color="secondary"
											disabled={!Boolean(form.getValues(`nodes.${nodeIndex}.address`))}
											aria-label={t("route/node/geolocate")}
										>
											<LocationOnIcon />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
						afterChange={() => {
							const currentValue = form.getValues(`nodes.${nodeIndex}.address`);
							handleAddressChange(currentValue as string);
						}}
						onFocus={() => {
							const currentValue = form.getValues(`nodes.${nodeIndex}.address`);
							if (currentValue && (currentValue as string).length >= 1 && textFieldRef.current) {
								setAnchorEl(textFieldRef.current);
							}
						}}
					/>
				</Box>

				<AddressAutocomplete
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					results={activeResults}
					isLoading={isLoadingOrDebouncing}
					searchMode={currentSearchMode}
					onSelect={handleSelectAddress}
					onClose={handleCloseAutocomplete}
					onTabChange={handleTabChange}
				/>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
						width: ROUTE_NODE_TRAILING_WIDTH,
					}}
				>
					<IconButton
						size="small"
						onClick={onDelete}
						disabled={!canDelete}
						aria-label={t("route/node/remove")}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};

export default RouteNodeInput;
